/**
 * PÁGINA · Configurações (`/settings`) — o hub da conta e das empresas.
 *
 * ─── O DESENHO OFICIAL NUMA TELA DE ABAS QUE É UM HUB ───────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`, `/financeiro` e `/relatorios`.
 * O que precisou ser TRADUZIDO aqui:
 *
 * · O EYEBROW diz a FAMÍLIA ("Conta") e o H1 acompanha a ABA — o arranjo de
 *   `/quotes` e `/financeiro` (#188/#200). Um h1 fixo em "Configurações" com a
 *   contagem de empresas embaixo seria a mentira do #182 uma linha acima do
 *   número.
 *
 * · O VALOR AO VIVO MUDA DE PERGUNTA POR ABA, e nenhuma das duas tem dinheiro:
 *     Minha conta → o NOME ao vivo (a tradução de identidade dos cadastros,
 *                   #160/#204 — ele muda enquanto o usuário digita), com o
 *                   e-mail na sublinha, que é o que diz em qual conta se está;
 *     Empresas    → a CONTAGEM (a tradução de `/clients` e `/catalog`, #195),
 *                   com a empresa ativa e o limite do plano na sublinha.
 *   Dinheiro seria impossível aqui, e contagem numa tela de UM registro também
 *   — sobra a identidade, exatamente como no formulário de cliente.
 *
 * · O CARTÃO QUE REPETIA O HERO SAIU (#200): o e-mail vivia num cartão "Conta"
 *   junto do plano; hoje ele é a sublinha do hero e o cartão ficou só com o
 *   plano, que é o que tem AÇÃO (o upgrade). O mesmo vale para a lista de
 *   empresas, que perdeu o próprio cabeçalho — contagem, limite do plano e
 *   "Nova empresa" passaram a ser hero (`showHeader={false}`).
 *
 * · As ABAS continuam `<Tabs>` (azul, herdado do componente base — #25): aqui
 *   elas SÃO troca de contexto, e continuam CONTROLADAS PELA URL (`?tab=`),
 *   porque é assim que o `WelcomeTour` troca de aba navegando.
 *
 * · NÃO há barra flutuante de salvar. Esta tela é um hub, não um formulário: o
 *   único campo editável é o nome, e o botão dele mora dentro do próprio cartão
 *   (escopo explícito). Uma barra colada na navegação inferior anunciaria
 *   "salvar" enquanto o usuário lê os termos de uso ou a zona de risco.
 *
 * · Os quatro `data-tour` (conta, perfil, ações, indicação) e o
 *   `tour-add-company` são ÂNCORAS do onboarding: mover um deles sem mover o
 *   atributo junto faz o coach-mark apontar para o vazio, sem erro nenhum.
 */
import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Settings,
  LogOut,
  Loader2,
  Trash2,
  User,
  Crown,
  Key,
  RefreshCw,
  FileText,
  HelpCircle,
  Shield,
  WifiOff,
  Receipt,
  Gift,
  Share2,
  Copy,
  Users,
  MessageSquareText,
  ArrowLeft,
  AlertTriangle,
  Plus,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { useGlobalPaywall } from '@/contexts/PaywallContext';
import { CompanyList } from '@/components/company/CompanyList';
import { useNewCompanyAction } from '@/components/company/useNewCompanyAction';
import { useCompany } from '@/contexts/useCompany';
import { PaymentDetectionCard } from '@/components/payments/PaymentDetectionCard';
import { CONTENT_CARD, FIELD_INPUT, FieldLabel } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useRole } from '@/hooks/useRole';
import { useFeature } from '@/hooks/useFeature';
import { useFiscalReadiness } from '@/hooks/useFiscalReadiness';
import { FiscalTags } from '@/components/nfse/FiscalTags';
import { getCachedProfile, cacheProfile } from '@/lib/offlineStorage';
import { addToOfflineQueue } from '@/lib/offlineStorage';
import { clearAllDataCache, getCachedProfile as getCachedProfileData, cacheProfile as cacheProfileData } from '@/lib/offlineDataCache';
import { clearImageCache } from '@/lib/imageCache';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SignOutOverlay } from '@/components/modals/SignOutOverlay';
import {
  copyReferralLink,
  getReferralLink,
  shareReferralLink,
  type ReferralStats,
} from '@/lib/referral';

type SettingsTab = 'profile' | 'companies';

/** O h1 do hero acompanha a aba; o eyebrow fica com a família (ver cabeçalho). */
const TAB_TITLES: Record<SettingsTab, string> = {
  profile: 'Minha conta',
  companies: 'Empresas',
};

export default function SettingsPage() {
  const { user, profile, signOut, refreshProfile } = useAuth();
  const { isPremium, planType } = useSubscription();
  const { isOnline } = useNetworkStatus();
  // Fiscal/NFS-e is owner/admin only, like finance.
  const { canSeeFinance, canManageTeam, canWrite } = useRole();
  const fiscalReadiness = useFiscalReadiness();
  // Só CRIAR modelos é PRO; o envio segue grátis com o texto padrão do app.
  const messageTemplatesFeature = useFeature('message_templates');
  const { companies, activeCompany } = useCompany();
  // Mesmo gate de plano do estado vazio da lista — uma implementação só.
  const { startNewCompany, multipleCompanies } = useNewCompanyAction();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab and action from URL params. A aba é CONTROLADA pela URL (?tab=) para
  // que o WelcomeTour consiga trocar de aba navegando — com defaultValue, mudar a
  // query após montado não alternava a aba.
  //
  // NORMALIZADA: `?tab=lixo` deixaria o `<Tabs>` sem conteúdo nenhum (nenhum
  // `TabsContent` casaria) e a tela apareceria vazia sob o hero, sem erro.
  const activeTab: SettingsTab = searchParams.get('tab') === 'companies' ? 'companies' : 'profile';
  const initialAction = searchParams.get('action') as 'new' | null;

  const handleTabChange = (tab: string) => {
    searchParams.set('tab', tab);
    setSearchParams(searchParams, { replace: true });
  };

  // Handle Stripe checkout cancel redirect
  useEffect(() => {
    const canceled = searchParams.get('canceled');
    if (canceled === 'true') {
      toast.info('Checkout cancelado. Você pode assinar a qualquer momento!', {
        duration: 4000,
        // Mantém a barra de progresso do toast em sincronia com a duração custom.
        style: { '--toast-duration': '4s' } as React.CSSProperties,
      });
      // Clean up the URL
      searchParams.delete('canceled');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const getPlanLabel = () => {
    switch (planType) {
      case 'admin':
        return { label: 'Admin', variant: 'default' as const };
      case 'pro':
        return { label: 'Pro', variant: 'default' as const };
      default:
        return { label: 'Gratuito', variant: 'secondary' as const };
    }
  };

  const planInfo = getPlanLabel();

  // Get full name from profile or cache for offline
  const cachedProfile = user ? getCachedProfile(user.id) : null;
  const initialFullName = profile?.full_name || cachedProfile?.full_name || '';

  const [fullName, setFullName] = useState(initialFullName);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [, openPaywall] = useGlobalPaywall();
  const [loggingOut, setLoggingOut] = useState(false);
  const [referralStats, setReferralStats] = useState<ReferralStats>({
    total_invited: 0,
    converted: 0,
    rewarded: 0,
    bonus_earned: 0,
  });
  const [loadingReferralStats, setLoadingReferralStats] = useState(false);

  const [clearingData, setClearingData] = useState(false);
  const [includeCompaniesInClear, setIncludeCompaniesInClear] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // "NOVO" badge for Consulta Fiscal — shown 30 days after user first sees the entry
  const [showFiscalNew, setShowFiscalNew] = useState(false);
  useEffect(() => {
    const KEY = 'orcamigo_fiscal_seen';
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    try {
      const raw = localStorage.getItem(KEY);
      const now = Date.now();
      if (!raw) {
        localStorage.setItem(KEY, String(now));
        setShowFiscalNew(true);
        return;
      }
      const seen = Number(raw);
      if (Number.isFinite(seen) && now - seen < THIRTY_DAYS) {
        setShowFiscalNew(true);
      }
    } catch {
      // localStorage unavailable — skip badge
    }
  }, []);

  // Load profile data - ensure all fields sync when profile loads or changes
  // CRITICAL: Also reset when profile becomes null (logout) or when user changes
  // Also use cached profile for offline scenarios
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
    } else if (user && cachedProfile) {
      // Use cached profile when offline
      setFullName(cachedProfile.full_name || '');
    } else {
      // Reset to empty when no profile (logged out or switching users)
      setFullName('');
    }
  }, [profile?.id, profile?.full_name, profile, user, cachedProfile]);

  const referralCode = profile?.referral_code || cachedProfile?.referral_code || null;
  const referralLink = referralCode ? getReferralLink(referralCode) : '';

  useEffect(() => {
    if (!user?.id || !isOnline) return;

    let cancelled = false;
    const loadReferralStats = async () => {
      setLoadingReferralStats(true);
      const { data, error } = await supabase
        .from('referrals')
        .select('status, reward_type, reward_amount')
        .eq('referrer_id', user.id);

      if (!cancelled) {
        if (error) {
          console.error('[SettingsPage] Error loading referral stats:', error);
        } else {
          const rows = data ?? [];
          setReferralStats({
            total_invited: rows.length,
            converted: rows.filter((row) => row.status === 'converted' || row.status === 'rewarded').length,
            rewarded: rows.filter((row) => row.status === 'rewarded').length,
            bonus_earned: rows.reduce(
              (total, row) => total + (row.reward_type === 'free_quotes' ? row.reward_amount ?? 0 : 0),
              0,
            ),
          });
        }
        setLoadingReferralStats(false);
      }
    };

    loadReferralStats();
    return () => {
      cancelled = true;
    };
  }, [user?.id, isOnline]);

  const handleShareReferral = async () => {
    if (!referralCode) return;
    try {
      await shareReferralLink(referralCode);
    } catch {
      toast.error('Não foi possível compartilhar o link.');
    }
  };

  const handleCopyReferral = async () => {
    if (!referralCode) return;
    try {
      await copyReferralLink(referralCode);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    
    setSaving(true);

    try {
      // If offline, queue the operation and update local cache
      if (!isOnline) {
        addToOfflineQueue('update_profile', {
          userId: user.id,
          updates: { full_name: fullName.trim() || null },
        });
        
        // Update local cache immediately
        const currentCachedProfile = getCachedProfileData(user.id);
        if (currentCachedProfile) {
          cacheProfileData(user.id, { ...currentCachedProfile, full_name: fullName.trim() || null });
        }
        
        toast.success('Alterações salvas localmente! Serão sincronizadas quando houver internet.', {
          icon: '📴',
        });
        return;
      }
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim() || null,
        })
        .eq('id', user.id);

      if (profileError) {
        const msg = (profileError as any)?.message as string | undefined;
        toast.error(`Erro ao salvar perfil${msg ? `: ${msg}` : ''}`);
        return;
      }

      toast.success('Perfil atualizado com sucesso!');
      refreshProfile();
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      // Brief smooth delay for transition animation
      await new Promise((resolve) => setTimeout(resolve, 400));
      toast.success('Até logo!');
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Erro ao sair da conta. Tente novamente.');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleClearAllData = async () => {
    if (!user) return;
    setClearingData(true);

    try {
      // If online, delete from database
      if (isOnline) {
        // Delete user's quotes and items
        const { data: quotes } = await supabase
          .from('quotes')
          .select('id')
          .eq('user_id', user.id);

        if (quotes && quotes.length > 0) {
          const quoteIds = quotes.map(q => q.id);
          await supabase.from('quote_items').delete().in('quote_id', quoteIds);
          await supabase.from('quotes').delete().eq('user_id', user.id);
        }

        // Delete clients
        await supabase.from('clients').delete().eq('user_id', user.id);

        // Delete catalog items (products table)
        await supabase.from('products').delete().eq('user_id', user.id);

        // Optionally delete companies
        if (includeCompaniesInClear) {
          await supabase.from('companies').delete().eq('user_id', user.id);
          // Dispatch event to refresh company context
          window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId: null } }));
        }
      } else {
        // If offline, queue the operation for later sync
        addToOfflineQueue('clear_data', { 
          userId: user.id, 
          includeCompanies: includeCompaniesInClear 
        });
      }

      // Always clear local cache (localStorage snapshots + IndexedDB image bytes)
      clearAllDataCache();
      void clearImageCache();

      // Dispatch events to refresh UI
      window.dispatchEvent(new CustomEvent('quotes-updated'));
      window.dispatchEvent(new CustomEvent('clients-updated'));
      window.dispatchEvent(new CustomEvent('catalog-updated'));
      if (includeCompaniesInClear) {
        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId: null } }));
      }

      if (isOnline) {
        toast.success('Todos os dados foram apagados com sucesso!');
      } else {
        toast.success('Dados locais apagados. Sincronização pendente.');
      }
      setIncludeCompaniesInClear(false); // Reset checkbox
    } catch (error) {
      toast.error('Erro ao apagar dados. Tente novamente.');
    } finally {
      setClearingData(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.warning('Preencha todos os campos');
      return;
    }

    if (newPassword.length < 6) {
      toast.warning('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.warning('As senhas não coincidem');
      return;
    }

    setChangingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        toast.error(`Erro ao alterar senha: ${error.message}`);
        return;
      }

      toast.success('Senha alterada com sucesso!');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Erro ao alterar senha. Tente novamente.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleting(true);

    try {
      // Delete user's quotes and items first
      const { data: quotes } = await supabase
        .from('quotes')
        .select('id')
        .eq('user_id', user.id);

      if (quotes && quotes.length > 0) {
        const quoteIds = quotes.map(q => q.id);
        await supabase.from('quote_items').delete().in('quote_id', quoteIds);
        await supabase.from('quotes').delete().eq('user_id', user.id);
      }

      // Delete clients
      await supabase.from('clients').delete().eq('user_id', user.id);

      // Delete catalog items (products table)
      await supabase.from('products').delete().eq('user_id', user.id);

      // Delete companies
      await supabase.from('companies').delete().eq('user_id', user.id);

      // Delete profile
      await supabase.from('profiles').delete().eq('id', user.id);

      // Sign out
      await signOut();
      toast.success('Conta excluída com sucesso');
      navigate('/');
    } catch (error) {
      toast.error('Erro ao excluir conta. Tente novamente.');
    } finally {
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <MainLayout showFab={false}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Settings className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Configurações</h2>
          <p className="mb-4 text-muted-foreground">
            Faça login para acessar as configurações
          </p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  /**
   * A SUBLINHA da aba "Empresas": o limite do plano (que era o subtítulo do
   * cabeçalho da lista e não podia se perder ao subir para o hero) e a empresa
   * ativa.
   *
   * ⚠️ A ORDEM foi decidida MEDINDO, não por gosto: a linha é `truncate`, e com
   * uma razão social longa ("68.129.625 JOAQUIM RODRIGUES DA COSTA" é real neste
   * app) o corte comia exatamente o "N/1 no plano Gratuito" — a única coisa na
   * tela que explica por que criar a segunda empresa abre a paywall. O nome da
   * empresa ativa, ao contrário, está repetido no cartão marcado logo abaixo:
   * quem cede é ele. É a régua do #52 (selo ao lado de nome), do outro lado.
   */
  const companiesHint =
    [
      multipleCompanies.allowed ? null : `${companies.length}/1 no plano Gratuito`,
      activeCompany?.name ? `Ativa: ${activeCompany.name}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'Nenhuma empresa cadastrada';

  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        {/* Voltar: esta era a ÚNICA tela do app sem ele. Quem entrou por engano
            (o ícone de perfil do dashboard é a porta) ficava dependendo do botão
            físico do Android — que nem todo aparelho expõe da mesma forma. */}
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">Conta</p>
          <h1 className="truncate text-lg font-bold leading-tight">{TAB_TITLES[activeTab]}</h1>
        </div>

        {/* A ação PRIMÁRIA da aba de empresas mora no hero (#186/#188), com o
            mesmo gate de plano do estado vazio da lista. O `data-tour` veio
            junto: sem ele o coach-mark do onboarding apontaria para o vazio. */}
        {activeTab === 'companies' && (
          <Button3D
            size="sm"
            color="light"
            className="shrink-0"
            data-tour="tour-add-company"
            onClick={startNewCompany}
            aria-label="Nova empresa"
            title="Nova empresa"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Nova
          </Button3D>
        )}
      </div>

      {activeTab === 'profile' ? (
        /* O NOME AO VIVO — ele acompanha o que está sendo digitado no campo lá
           embaixo, como o total acompanha os itens em `/quotes/new`.
           `line-clamp-2` + `break-words`, nunca `truncate`: aqui o valor é texto
           livre, e um nome longo tem que quebrar em vez de sumir (#160/#175). */
        <div className="relative mt-5 min-w-0">
          <p className="text-xs text-white/75">Seu nome</p>
          <p className="line-clamp-2 break-words text-2xl font-bold leading-tight">
            {fullName.trim() || <span className="text-white/60">Sem nome</span>}
          </p>
          <p className="mt-1 truncate text-xs text-white/70">{user?.email}</p>
        </div>
      ) : (
        /* Numa tela sem dinheiro o valor ao vivo é a CONTAGEM (#195), e o rótulo
           é o que a torna honesta. */
        <div className="relative mt-5 min-w-0">
          <p className="text-xs text-white/75">Empresas cadastradas</p>
          <p className="text-3xl font-bold leading-tight">{companies.length}</p>
          <p className="mt-1 truncate text-xs text-white/70">{companiesHint}</p>
        </div>
      )}
    </div>
  );

  return (
    <MainLayout showFab={false}>
      <div className="pb-4">
        {hero}

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          {/* Alternador-pill padrão (azul) — estilo no componente base ui/tabs. */}
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile">Minha conta</TabsTrigger>
            <TabsTrigger value="companies">Empresas</TabsTrigger>
          </TabsList>

          <TabsContent value="companies" className="mt-5 space-y-4">
            {/*
              O interruptor é da EMPRESA ATIVA, por isso mora nesta aba e não em
              "Minha conta". Ele some sozinho quando o papel não vê o financeiro
              ou a feature está bloqueada — e ela está, para TODO usuário real
              (grátis e PRO), inclusive no lançamento: o BaaS do Asaas depende de
              habilitação manual que hoje esbarra no CNPJ MEI. Ou seja, este
              bloco não renderiza nada hoje; ele fica aqui porque o caminho de
              teste (admin) continua exercitando a integração já provada em
              sandbox. O porquê inteiro está em `src/lib/features.ts`.
            */}
            <PaymentDetectionCard />
            {/* `showHeader={false}`: contagem, limite do plano e "Nova empresa"
                subiram para o hero — repetir os três aqui seria a duplicação
                que o #200 removeu do Financeiro. */}
            <CompanyList initialAction={initialAction} showHeader={false} />
          </TabsContent>

          <TabsContent value="profile" className="mt-5 space-y-4">
            {/* PLANO — o cartão da "Conta" ficou só com ele: o e-mail virou a
                sublinha do hero, e o que sobra aqui é o que tem AÇÃO. */}
            <div data-tour="tour-account" className={cn(CONTENT_CARD, 'p-4')}>
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isPremium ? 'bg-warning/20' : 'bg-accent'}`}>
                  <Crown className={`h-4 w-4 ${isPremium ? 'text-warning' : 'text-muted-foreground'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Plano atual</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge variant={planInfo.variant} className={isPremium ? 'bg-warning text-warning-foreground' : ''}>
                      {planInfo.label}
                    </Badge>
                    {!isPremium && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-xs border-warning text-warning hover:bg-warning hover:text-warning-foreground"
                        onClick={() => openPaywall({ reason: 'feature' })}
                      >
                        <Crown className="h-3 w-3 mr-1" />
                        Upgrade
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* SEU PERFIL — o único campo editável da tela. O botão de salvar
                mora DENTRO do cartão (escopo explícito: ele salva este campo),
                e não numa barra flutuante: esta tela é um hub, e a barra ficaria
                anunciando "salvar" enquanto se lê a zona de risco. */}
            <div data-tour="tour-your-profile" className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
              <div className="space-y-1.5">
                <FieldLabel icon={User} htmlFor="settings-full-name">
                  Seu nome
                </FieldLabel>
                <Input
                  id="settings-full-name"
                  placeholder="Digite seu nome"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={FIELD_INPUT}
                />
                <p className="text-xs text-muted-foreground">
                  Este nome aparecerá na saudação da tela inicial.
                </p>
              </div>

              <Button3D
                className="w-full"
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar alterações'
                )}
              </Button3D>
            </div>

            {/* Actions Section */}
            <div data-tour="tour-account-actions" className="space-y-4">
              <div className={cn(CONTENT_CARD, 'space-y-2 p-4')}>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Ações da conta
                </p>

                {/* Change Password Button - Only for email provider and online */}
                {user?.app_metadata?.provider === 'email' || !user?.app_metadata?.provider ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block">
                        <Button3D
                          className="w-full"
                          onClick={() => setShowPasswordModal(true)}
                          disabled={!isOnline}
                        >
                          <Key className="mr-2 h-4 w-4" />
                          Alterar senha
                          {!isOnline && <WifiOff className="ml-2 h-4 w-4" />}
                        </Button3D>
                      </span>
                    </TooltipTrigger>
                    {!isOnline && (
                      <TooltipContent>
                        <p>Requer conexão com internet</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full h-12 opacity-60 cursor-not-allowed"
                    disabled
                  >
                    <Key className="mr-2 h-4 w-4" />
                    Gerenciado pelo {user?.app_metadata?.provider === 'google' ? 'Google' : 'Provedor externo'}
                  </Button>
                )}

                {/* Logout Button */}
                <Button3D
                  color="gray"
                  className="w-full"
                  onClick={handleLogout}
                  disabled={loggingOut}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair da conta
                </Button3D>
              </div>

              {/* Team Section (owner/admin only; "Modelos de mensagens" também
                  para membros com permissão de escrita, que podem editá-los) */}
              {(canManageTeam || canWrite) && (
                <div className={cn(CONTENT_CARD, 'p-2')}>
                  <p className="px-2 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Empresa
                  </p>
                  <div className="space-y-1">
                    {canManageTeam && (
                      <>
                        <Link to="/equipe" className="block">
                          <Button
                            variant="ghost"
                            className="w-full h-11 justify-start rounded-xl text-foreground"
                          >
                            <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                            Equipe
                          </Button>
                        </Link>
                        <Link to="/nfse" className="block">
                          <Button
                            variant="ghost"
                            className="w-full h-11 justify-start rounded-xl text-foreground"
                          >
                            <Receipt className="mr-2 h-4 w-4 text-muted-foreground" />
                            Emitir NFS-e
                            {/* Tags PRO/CNPJ sinalizam o pré-requisito que falta; o link
                                continua clicável (o /nfse explica/oferece o upgrade).
                                Esconde durante o load p/ não piscar 'CNPJ' a um MEI. */}
                            <FiscalTags
                              tags={fiscalReadiness.isLoading ? [] : fiscalReadiness.tags}
                              className="ml-2"
                            />
                          </Button>
                        </Link>
                      </>
                    )}
                    {canWrite && (
                      <Link to="/modelos-mensagens" className="block">
                        <Button
                          variant="ghost"
                          className="w-full h-11 justify-start rounded-xl text-foreground"
                        >
                          <MessageSquareText className="mr-2 h-4 w-4 text-muted-foreground" />
                          Modelos de mensagens
                          {/* Selo PRO na pílula única do app (CLAUDE.md #24); o link
                              continua clicável — a própria tela explica/oferece o upgrade. */}
                          {messageTemplatesFeature.upsell && <FiscalTags tags={['PRO']} className="ml-2" />}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* Help & Legal Links Section */}
              <div className={cn(CONTENT_CARD, 'p-2')}>
                <p className="px-2 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Ajuda &amp; legal
                </p>
                <div className="space-y-1">
                  <Link to="/help" className="block">
                    <Button
                      variant="ghost"
                      className="w-full h-11 justify-start rounded-xl text-foreground"
                    >
                      <HelpCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                      Central de ajuda
                    </Button>
                  </Link>
                  {canSeeFinance && (
                    <Link to="/fiscal" className="block">
                      <Button
                        variant="ghost"
                        className="w-full h-11 justify-start rounded-xl text-foreground"
                      >
                        <Receipt className="mr-2 h-4 w-4 text-muted-foreground" />
                        Consulta fiscal (NFS-e)
                        {showFiscalNew && (
                          <Badge className="ml-2 bg-emerald-500 text-white text-[10px] px-1.5 py-0">
                            NOVO
                          </Badge>
                        )}
                      </Button>
                    </Link>
                  )}
                  <Link to="/terms" className="block">
                    <Button
                      variant="ghost"
                      className="w-full h-11 justify-start rounded-xl text-foreground"
                    >
                      <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                      Termos de uso
                    </Button>
                  </Link>
                  <Link to="/privacy" className="block">
                    <Button
                      variant="ghost"
                      className="w-full h-11 justify-start rounded-xl text-foreground"
                    >
                      <Shield className="mr-2 h-4 w-4 text-muted-foreground" />
                      Política de privacidade
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Referral Program */}
            <div
              data-tour="tour-referral"
              className="overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-4 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                  <Gift className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-foreground">Indique e ganhe</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Seu amigo ganha +3 orçamentos. Você recebe +5 no plano Gratuito ou +7 dias se for PRO.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-border/70 bg-background/80 p-3">
                <p className="mb-1 text-xs font-medium text-muted-foreground">Seu link de indicação</p>
                <p className="break-all text-sm font-medium text-foreground">
                  {referralLink || 'Preparando seu link...'}
                </p>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button3D onClick={handleShareReferral} disabled={!referralCode}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Compartilhar
                </Button3D>
                <Button3D color="gray" onClick={handleCopyReferral} disabled={!referralCode}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copiar link
                </Button3D>
              </div>

              <div className="mt-4 flex items-center justify-between rounded-xl bg-primary/10 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Indicações convertidas</span>
                <span className="font-semibold text-primary">
                  {loadingReferralStats ? '...' : referralStats.converted}
                </span>
              </div>
            </div>

            {/*
              ZONA DE RISCO — separada, e de propósito a última coisa da tela.

              Antes, "Limpar todos os dados" e "Excluir conta" eram dois pills
              vermelhos do MESMO tamanho de "Alterar senha" e "Sair da conta",
              empilhados na mesma lista. Quatro botões grandes iguais, dois deles
              irreversíveis, no caminho de quem só queria trocar a senha — a cor
              era a única coisa separando rotina de destruição, e cor sozinha não
              basta (nem para quem tem daltonismo, nem para quem toca rápido).

              Aqui elas saem do fluxo e ganham um cabeçalho que diz o que são.
              O diálogo de confirmação de cada uma continua igual — esta é uma
              camada A MAIS, não a substituta.
            */}
            {/* Sem `mt-8`: margem negativa/extra num filho direto perde a briga
                de especificidade contra o `space-y-*` do container (#194) — a
                separação aqui vem da borda e do fundo destrutivos, não de uma
                classe que o CSS descartaria em silêncio. */}
            <div className="rounded-2xl border border-destructive/30 bg-destructive/[0.03] p-4">
              <div className="mb-1 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <h3 className="text-sm font-semibold text-destructive">Zona de risco</h3>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Estas ações não têm volta.
              </p>
              <div className="space-y-2">
              {/* Clear All Data Button */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  {/* Ghost, não pill 3D: dentro da zona de risco o peso
                      visual de CTA convidaria ao toque — é o oposto do que se
                      quer de uma ação sem volta. */}
                  <Button
                    variant="ghost"
                    className="h-11 w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Limpar todos os dados
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Limpar todos os dados?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3">
                        <p>
                          Esta ação apagará permanentemente:
                        </p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                          <li>Todos os orçamentos</li>
                          <li>Todos os clientes</li>
                          <li>Todos os serviços e produtos do catálogo</li>
                        </ul>
                        
                        <div className="flex items-center space-x-2 pt-2 border-t">
                          <Checkbox 
                            id="include-companies" 
                            checked={includeCompaniesInClear}
                            onCheckedChange={(checked) => setIncludeCompaniesInClear(checked === true)}
                          />
                          <label 
                            htmlFor="include-companies" 
                            className="text-sm font-medium leading-none cursor-pointer text-foreground"
                          >
                            Incluir empresas na exclusão
                          </label>
                        </div>
                        
                        <p className={`font-medium mt-2 ${includeCompaniesInClear ? 'text-destructive' : 'text-amber-600'}`}>
                          {includeCompaniesInClear 
                            ? '⚠️ Atenção: suas empresas também serão excluídas!' 
                            : '⚠️ Sua conta e empresas serão mantidas.'}
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setIncludeCompaniesInClear(false)}>
                      Cancelar
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleClearAllData}
                      disabled={clearingData}
                      className={includeCompaniesInClear 
                        ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" 
                        : "bg-amber-500 text-white hover:bg-amber-600"}
                    >
                      {clearingData ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Apagando...
                        </>
                      ) : (
                        'Sim, limpar tudo'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Delete Account Button - requires online */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-11 w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={!isOnline}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir conta
                          {!isOnline && <WifiOff className="ml-2 h-4 w-4" />}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir conta permanentemente?</AlertDialogTitle>
                          <AlertDialogDescription className="space-y-2">
                            <p>
                              Esta ação é irreversível. Todos os seus dados serão apagados permanentemente:
                            </p>
                            <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                              <li>Orçamentos e histórico</li>
                              <li>Clientes cadastrados</li>
                              <li>Catálogo de serviços</li>
                              <li>Empresas cadastradas</li>
                              <li>Configurações de perfil</li>
                            </ul>
                            <p className="font-medium text-destructive mt-3">
                              ⚠️ Qualquer assinatura ativa não será reembolsada.
                            </p>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteAccount}
                            disabled={deleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {deleting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Excluindo...
                              </>
                            ) : (
                              'Sim, excluir minha conta'
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </span>
                </TooltipTrigger>
                {!isOnline && (
                  <TooltipContent>
                    <p>Requer conexão com internet</p>
                  </TooltipContent>
                )}
              </Tooltip>

              </div>
            </div>

            {/* Password Change Modal */}
            <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Alterar senha</DialogTitle>
                  <DialogDescription>
                    Digite sua nova senha abaixo.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nova senha</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmar senha</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Digite novamente"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button3D
                    color="gray"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    Cancelar
                  </Button3D>
                  <Button3D
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Alterando...
                      </>
                    ) : (
                      'Alterar senha'
                    )}
                  </Button3D>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {/* Sign Out Loading Animation Overlay */}
            <SignOutOverlay open={loggingOut} />
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
